/*
 * Copyright 2013 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { utf8encode } from '@awayjs/graphics';
export var StreamNoDataError = {};
var masks = new Uint32Array(33);
for (var i = 1, mask = 0; i <= 32; ++i) {
    masks[i] = mask = (mask << 1) | 1;
}
var Stream = /** @class */ (function () {
    function Stream(buffer, offset, length, maxLength) {
        if (offset === undefined)
            offset = 0;
        if (buffer.buffer instanceof ArrayBuffer) {
            offset += buffer.byteOffset;
            buffer = buffer.buffer;
        }
        if (length === undefined)
            length = buffer.byteLength - offset;
        if (maxLength === undefined)
            maxLength = length;
        this.bytes = new Uint8Array(buffer, offset, maxLength);
        this.view = new DataView(buffer, offset, maxLength);
        this.pos = 0;
        this.end = length;
        this.bitBuffer = 0;
        this.bitLength = 0;
    }
    Stream.prototype.align = function () {
        this.bitBuffer = this.bitLength = 0;
    };
    Stream.prototype.ensure = function (size) {
        if (this.pos + size > this.end) {
            throw StreamNoDataError;
        }
    };
    Stream.prototype.remaining = function () {
        return this.end - this.pos;
    };
    Stream.prototype.substream = function (begin, end) {
        var stream = new Stream(this.bytes);
        stream.pos = begin;
        stream.end = end;
        return stream;
    };
    Stream.prototype.push = function (data) {
        var bytes = this.bytes;
        var newBytesLength = this.end + data.length;
        if (newBytesLength > bytes.length) {
            throw 'stream buffer overfow';
        }
        bytes.set(data, this.end);
        this.end = newBytesLength;
    };
    Stream.prototype.readSi8 = function () {
        return this.view.getInt8(this.pos++);
    };
    Stream.prototype.readSi16 = function () {
        var r = this.view.getInt16(this.pos, true);
        this.pos += 2;
        return r;
    };
    Stream.prototype.readSi32 = function () {
        var r = this.view.getInt32(this.pos, true);
        this.pos += 4;
        return r;
    };
    Stream.prototype.readUi8 = function () {
        return this.bytes[this.pos++];
    };
    Stream.prototype.readUi16 = function () {
        var r = this.view.getUint16(this.pos, true);
        this.pos += 2;
        return r;
    };
    Stream.prototype.readUi32 = function () {
        var r = this.view.getUint32(this.pos, true);
        this.pos += 4;
        return r;
    };
    Stream.prototype.readFixed = function () {
        var r = this.view.getInt32(this.pos, true) / 65536;
        this.pos += 4;
        return r;
    };
    Stream.prototype.readFixed8 = function () {
        var r = this.view.getInt16(this.pos, true) / 256;
        this.pos += 2;
        return r;
    };
    Stream.prototype.readFloat16 = function () {
        var ui16 = this.view.getUint16(this.pos, false);
        this.pos += 2;
        var sign = ui16 >> 15 ? -1 : 1;
        var exponent = (ui16 & 0x7c00) >> 10;
        var fraction = ui16 & 0x03ff;
        if (!exponent)
            return sign * Math.pow(2, -14) * (fraction / 1024);
        if (exponent === 0x1f)
            return fraction ? NaN : sign * Infinity;
        return sign * Math.pow(2, exponent - 15) * (1 + (fraction / 1024));
    };
    Stream.prototype.readFloat = function () {
        var r = this.view.getFloat32(this.pos, true);
        this.pos += 4;
        return r;
    };
    Stream.prototype.readDouble = function () {
        var r = this.view.getFloat64(this.pos, true);
        this.pos += 8;
        return r;
    };
    Stream.prototype.readEncodedU32 = function () {
        var bytes = this.bytes;
        var val = bytes[this.pos++];
        if (!(val & 0x080))
            return val;
        val = (val & 0x7f) | bytes[this.pos++] << 7;
        if (!(val & 0x4000))
            return val;
        val = (val & 0x3fff) | bytes[this.pos++] << 14;
        if (!(val & 0x200000))
            return val;
        val = (val & 0x1FFFFF) | bytes[this.pos++] << 21;
        if (!(val & 0x10000000))
            return val;
        return (val & 0xFFFFFFF) | (bytes[this.pos++] << 28);
    };
    Stream.prototype.readBool = function () {
        return !!this.bytes[this.pos++];
    };
    Stream.prototype.readSb = function (size) {
        return (this.readUb(size) << (32 - size)) >> (32 - size);
    };
    Stream.prototype.readUb = function (size) {
        var buffer = this.bitBuffer;
        var bitlen = this.bitLength;
        var val = 0;
        while (size > bitlen) {
            if (bitlen > 24) {
                // Avoid overflow. Save current buffer in val and add remaining bits later.
                size -= bitlen;
                val = buffer << size;
                bitlen = 0;
            }
            buffer = (buffer << 8) | this.bytes[this.pos++];
            bitlen += 8;
        }
        bitlen -= size;
        val |= (buffer >>> bitlen) & masks[size];
        this.bitBuffer = buffer;
        this.bitLength = bitlen;
        return val;
    };
    Stream.prototype.readFb = function (size) {
        return this.readSb(size) / 65536;
    };
    Stream.prototype.readString = function (length) {
        var bytes = this.bytes;
        var codes;
        var pos = this.pos;
        if (length > -1) {
            codes = bytes.subarray(pos, pos += length);
        }
        else {
            length = 0;
            for (var i = pos; bytes[i]; i++) {
                length++;
            }
            codes = bytes.subarray(pos, pos += length);
            pos++;
        }
        this.pos = pos;
        var str = utf8encode(codes);
        if (str.indexOf('\0') >= 0) {
            str = str.split('\0').join('');
        }
        return str;
    };
    return Stream;
}());
export { Stream };
